import { useState } from 'react'
import { Check, Plus, Trash2, X } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type {
  OrderingMissing,
  PurchaseOrderingConfig,
  PurchaseOrderingConfigBody,
} from '@/api/endpoints/purchase-orders'
import { isValidGln } from '@/features/purchase-orders/lib/gln'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

/** The server's limit on copy recipients. */
const MAX_COPY_RECIPIENTS = 10

/** Everything a Standort needs before it can send, in the server's order. */
const REQUIREMENTS: Array<OrderingMissing> = [
  'disabled',
  'supplier_name',
  'supplier_gln',
  'edi_recipient',
  'buyer_gln',
]

const blankToNull = (value: string) =>
  value.trim() === '' ? null : value.trim()

/**
 * Standort → Bestellung (CHG-2026-10-edi-purchase-orders, design D7): the supplier, its GLN, the
 * EDI address, the copy recipients, the cutoff and the pre-order default. The checklist shows the
 * saved state, i.e. what the server would refuse a send for.
 */
export function PurchaseOrderingForm({
  config,
  saving,
  onSave,
}: {
  config: PurchaseOrderingConfig
  saving: boolean
  onSave: (body: PurchaseOrderingConfigBody) => void
}) {
  const { t } = useTranslation('purchase-orders')
  const [enabled, setEnabled] = useState(config.enabled)
  const [supplierName, setSupplierName] = useState(config.supplierName ?? '')
  const [supplierGln, setSupplierGln] = useState(config.supplierGln ?? '')
  const [ediRecipient, setEdiRecipient] = useState(config.ediRecipient ?? '')
  const [copies, setCopies] = useState<Array<string>>(config.copyRecipients)
  const [cutoff, setCutoff] = useState(config.orderCutoffTime)
  const [countsDefault, setCountsDefault] = useState(
    config.countsForPreordersDefault,
  )

  const glnInvalid =
    supplierGln.trim() !== '' && !isValidGln(supplierGln.trim())

  const submit = () => {
    if (glnInvalid) return
    onSave({
      enabled,
      supplierName: blankToNull(supplierName),
      supplierGln: blankToNull(supplierGln),
      ediRecipient: blankToNull(ediRecipient),
      copyRecipients: copies.map((c) => c.trim()).filter((c) => c !== ''),
      orderCutoffTime: cutoff,
      countsForPreordersDefault: countsDefault,
    })
  }

  return (
    <div className="grid max-w-4xl gap-8 lg:grid-cols-[minmax(0,1fr)_16rem]">
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-3">
          <Switch
            id="po-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
          <Label htmlFor="po-enabled">{t('config.enabled')}</Label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="po-supplier">{t('config.supplierName')}</Label>
            <Input
              id="po-supplier"
              value={supplierName}
              maxLength={128}
              onChange={(e) => setSupplierName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="po-supplier-gln">{t('config.supplierGln')}</Label>
            <Input
              id="po-supplier-gln"
              value={supplierGln}
              inputMode="numeric"
              maxLength={13}
              autoComplete="off"
              className="font-mono"
              aria-invalid={glnInvalid || undefined}
              onChange={(e) => setSupplierGln(e.target.value)}
            />
            {glnInvalid && (
              <p className="text-sm text-destructive">
                {t('config.glnInvalid')}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="po-edi">{t('config.ediRecipient')}</Label>
          <Input
            id="po-edi"
            type="email"
            value={ediRecipient}
            autoComplete="off"
            onChange={(e) => setEdiRecipient(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label>{t('config.copyRecipients')}</Label>
          {copies.map((copy, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                type="email"
                value={copy}
                autoComplete="off"
                aria-label={`${t('config.copyRecipients')} ${index + 1}`}
                onChange={(e) =>
                  setCopies((prev) =>
                    prev.map((c, i) => (i === index ? e.target.value : c)),
                  )
                }
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`${t('config.removeCopy')} ${index + 1}`}
                onClick={() =>
                  setCopies((prev) => prev.filter((_, i) => i !== index))
                }
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={copies.length >= MAX_COPY_RECIPIENTS}
              onClick={() => setCopies((prev) => [...prev, ''])}
            >
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
              {t('config.addCopy')}
            </Button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="po-cutoff">{t('config.cutoff')}</Label>
            <Input
              id="po-cutoff"
              type="time"
              value={cutoff}
              className="w-32"
              onChange={(e) => setCutoff(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="po-counts-default">
              {t('config.countsForPreordersDefault')}
            </Label>
            <div className="flex h-9 items-center">
              <Switch
                id="po-counts-default"
                checked={countsDefault}
                onCheckedChange={setCountsDefault}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label>{t('config.buyerGln')}</Label>
          <p className="font-mono text-sm">{config.buyerGln ?? '–'}</p>
          {!config.buyerGln && (
            <Alert className="border-warning/50 bg-warning/10">
              <AlertDescription>{t('config.buyerGlnMissing')}</AlertDescription>
            </Alert>
          )}
        </div>

        <div>
          <Button onClick={submit} disabled={saving || glnInvalid}>
            {t('config.save')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-lg border bg-muted/30 p-4 self-start">
        <p className="text-sm font-medium">
          {config.configured ? t('config.ready') : t('config.notReady')}
        </p>
        <ul className="flex flex-col gap-1.5 text-sm">
          {REQUIREMENTS.map((item) => {
            const missing = config.missing.includes(item)
            return (
              <li
                key={item}
                className={cn(
                  'flex items-center gap-2',
                  missing ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                {missing ? (
                  <X className="h-4 w-4 shrink-0" aria-hidden="true" />
                ) : (
                  <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
                )}
                <span>{t(`config.missing.${item}`)}</span>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
